import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { CommonModule } from '@angular/common';
import type { ElementRef, OnInit } from '@angular/core';
import { Component, NgModule, ViewChild } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import type { Observable } from 'rxjs';
import { EMPTY, map, of, startWith, switchMap } from 'rxjs';
import type { DisciplinedInterface } from 'src/app/shared/models/disciplined.interface';
import type { KeywordInterface } from 'src/app/shared/models/keyword.interface';
import { AppStateService } from 'src/app/shared/services/app-state.service';
import { DisciplinedService } from 'src/app/shared/services/disciplined.service';
import { KeywordService } from 'src/app/shared/services/keyword.service';
import type { IDialogContentComponent } from '../../../modal-dialog/modal-dialog.service';

/** Sentinel value pushed as a mat-option value to trigger keyword creation mode. */
export const CREATE_KEYWORD_OPTION = '__create_keyword__';

@Component({
  selector: 'app-modify-keywords',
  templateUrl: './modify-keywords.component.html',
  styleUrls: ['./modify-keywords.component.scss']
})
export class ModifyKeywordsComponent implements OnInit, IDialogContentComponent<number[], KeywordInterface[]> {
  @ViewChild('keywordInput') keywordInput: ElementRef<HTMLInputElement>;

  input?: KeywordInterface[];

  separatorKeysCodes: number[] = [ENTER, COMMA];
  keywordsCtrl = new FormControl();
  filteredKeywords$: Observable<KeywordInterface[]> = of([]);
  selectedKeywords: KeywordInterface[] = [];
  allKeywords$: Observable<KeywordInterface[]> = of([]);

  readonly createOptionValue = CREATE_KEYWORD_OPTION;

  /** Current trimmed text typed in the input, used to decide whether to show the create option. */
  typed = '';
  hasExactMatch = false;

  /** Create mode state. */
  isCreating = false;
  disciplineds: DisciplinedInterface[] = [];
  selectedDisciplinedId?: number;
  private allKeywordsCache: KeywordInterface[] = [];


  constructor(
    private readonly keywordService: KeywordService,
    private readonly disciplinedService: DisciplinedService,
    private readonly appStateService: AppStateService
  ) { }

  getResult(): number[] {
    return this.selectedKeywords.map(k => k.id);
  }

  isDisabled(): boolean {
    return false;
  }

  ngOnInit(): void {
    this.selectedKeywords = this.input ? this.input.slice() : [];
    if (this.appStateService.clinicId) {
      this.allKeywords$ = this.keywordService.getKeywords(this.appStateService.clinicId);
    }

    this.filteredKeywords$ = this.keywordsCtrl.valueChanges.pipe(
      startWith(null),
      switchMap((value) => {

        // Selecting the "Créer « ... »" option commits the sentinel string into the
        // control, which would otherwise overwrite `this.typed`. Ignore it so the typed
        // term (the label to create) is preserved when entering create mode.
        if (value === CREATE_KEYWORD_OPTION) {
          return EMPTY;
        }

        if (value && typeof (value) === 'string') {
          this.typed = value.trim();
          this.updateExactMatch(this.typed);
          return this.filter(value);
        }

        this.typed = '';
        this.hasExactMatch = false;
        return EMPTY;
      }),
    );
  }

  /** Whether the "Créer « ... »" option should be displayed. */
  get showCreateOption(): boolean {
    return this.typed.length > 0 && !this.hasExactMatch;
  }

  remove(index: number): void {
    if (index >= 0) {
      this.selectedKeywords.splice(index, 1);
    }
  }

  selected(event: MatAutocompleteSelectedEvent): void {
    if (event.option.value === CREATE_KEYWORD_OPTION) {
      this.enterCreateMode();
      return;
    }

    this.addKeyword(event.option.value as KeywordInterface);
  }

  /** Enters create mode and loads disciplines once. */
  enterCreateMode(): void {
    this.isCreating = true;
    this.selectedDisciplinedId = undefined;
    // Clear the autocomplete input so the sentinel value never lingers as visible text.
    // `this.typed` is preserved (it holds the label to create); it is reset by addKeyword/cancelCreate.
    if (this.keywordInput) {
      this.keywordInput.nativeElement.value = '';
    }
    this.keywordsCtrl.setValue(null, { emitEvent: false });
    if (this.appStateService.clinicId && this.disciplineds.length === 0) {
      this.disciplinedService.getDisciplineds(this.appStateService.clinicId)
        .subscribe(disciplineds => this.disciplineds = disciplineds);
    }
  }

  cancelCreate(): void {
    this.isCreating = false;
    this.selectedDisciplinedId = undefined;
  }

  /** Confirms creation of a new keyword with the chosen discipline. */
  confirmCreate(): void {
    if (!this.typed || this.selectedDisciplinedId === undefined) {
      return;
    }

    // Safety net: if a keyword with this label already exists (case-insensitive), reuse it
    // instead of creating a duplicate (guards against races / stale filter state).
    const target = this.typed.trim().toLowerCase();
    const existing = this.allKeywordsCache.find(k => k.label.trim().toLowerCase() === target);
    if (existing) {
      if (!this.selectedKeywords.some(k => k.id === existing.id)) {
        this.addKeyword(existing);
      }
      this.cancelCreate();
      return;
    }

    this.keywordService.addOrUpdateKeyword(this.typed, this.selectedDisciplinedId)
      .subscribe(keyword => {
        if (keyword) {
          this.addKeyword(keyword);
        }
        this.cancelCreate();
      });
  }

  private addKeyword(keyword: KeywordInterface): void {
    this.selectedKeywords.push(keyword);
    if (this.keywordInput) {
      this.keywordInput.nativeElement.value = '';
    }
    this.keywordsCtrl.setValue(null);
    this.typed = '';
    this.hasExactMatch = false;
  }

  private updateExactMatch(value: string): void {
    const target = value.trim().toLowerCase();
    this.hasExactMatch = !!target
      && this.allKeywordsCache.some(keyword => keyword.label.trim().toLowerCase() === target);
  }

  private filter(value: string): Observable<KeywordInterface[]> {
    const filterValue = value?.toLowerCase();
    return this.allKeywords$.pipe(map(keywords => {
      this.allKeywordsCache = keywords;
      this.updateExactMatch(value);
      return keywords.filter(keyword =>
        keyword.label.toLowerCase().includes(filterValue)
        && !this.selectedKeywords.map(k => k.id).includes(keyword.id));
    }));
  }
}

@NgModule({
  declarations: [
    ModifyKeywordsComponent
  ],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatChipsModule,
    MatAutocompleteModule,
    MatIconModule,
    MatSelectModule,
    MatButtonModule
  ],
  exports: [],
  providers: [ KeywordService ],
})
export class ModifyKeywordsModule {}
